import { CheckCircle2, Clock3, GlassWater, UserRound } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useLiveTable } from '../hooks/useLiveTable'
import { supabase } from '../lib/supabase'

type TicketStatus = 'confirmed'|'accepted'|'preparing'|'ready'|'delivered'|'cancelled'
type Ticket = { id:string; order_id:string; display_number:number; status:TicketStatus; production_sector:string; created_at:string }
type OrderItem = { id:string; order_id:string; product_name:string; quantity:number; notes:string|null; production_sector:string }
type Order = { id:string; table_id:string; customer_name:string|null }
type Table = { id:string; number:number; name:string|null }

const statusLabels:Record<TicketStatus,string> = {
  confirmed:'Aguardando separação', accepted:'Separando', preparing:'Separando', ready:'Pronto para retirada', delivered:'Entregue', cancelled:'Cancelado'
}
const actionLabels:Partial<Record<TicketStatus,string>> = { confirmed:'Separar e deixar pronto', ready:'Confirmar entrega' }

export function Reception(){
  const {rows,loading,error,reload}=useLiveTable<Ticket>('kitchen_tickets')
  const {rows:orderItems}=useLiveTable<OrderItem>('order_items')
  const {rows:orders}=useLiveTable<Order>('orders')
  const {rows:tables}=useLiveTable<Table>('dining_tables','number')
  const [updating,setUpdating]=useState<string|null>(null)

  async function advance(ticket:Ticket){
    if(!actionLabels[ticket.status])return
    setUpdating(ticket.id)
    const {data,error:requestError}=await supabase.functions.invoke('operational-actions',{body:{action:'advance_ticket',ticketId:ticket.id}})
    if(requestError||data?.error)window.alert(data?.error||requestError?.message||'Não foi possível atualizar o pedido.')
    else await reload()
    setUpdating(null)
  }

  const activeRows=useMemo(()=>rows.filter(ticket=>ticket.production_sector==='reception'&&!['delivered','cancelled'].includes(ticket.status)).sort((a,b)=>a.created_at.localeCompare(b.created_at)),[rows])
  const orderMap=new Map(orders.map(order=>[order.id,order]))
  const tableMap=new Map(tables.map(table=>[table.id,table]))

  return <>
    <div className="page-heading kds-heading"><div><span className="eyebrow dark">CAIXA / RECEPÇÃO</span><h1>Bebidas rápidas</h1><p>Cervejas, refrigerantes e itens sem preparo, organizados por mesa e retirada.</p></div><div className="kds-clock"><Clock3 size={18}/>{new Date().toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})}</div></div>
    {error&&<div className="form-error">{error}</div>}
    {loading?<div className="loading">Sincronizando recepção…</div>:activeRows.length?<div className="kds-grid">{activeRows.map(ticket=>{
      const order=orderMap.get(ticket.order_id)
      const table=order?tableMap.get(order.table_id):null
      const items=orderItems.filter(item=>item.order_id===ticket.order_id&&item.production_sector==='reception')
      const minutes=Math.max(0,Math.floor((Date.now()-new Date(ticket.created_at).getTime())/60000))
      return <article className={`ticket ticket-${ticket.status}`} key={ticket.id} style={{borderTopColor:ticket.status==='ready'?'#65c265':'#d6ff3f'}}>
        <header><div><span>MESA</span><strong style={{fontSize:26}}>{table?.number??'—'}</strong></div><div style={{textAlign:'right'}}><span>PEDIDO</span><strong>#{ticket.display_number}</strong></div></header>
        <div>
          <div style={{display:'flex',justifyContent:'space-between',gap:10,alignItems:'center'}}><b>{statusLabels[ticket.status]}</b><span className={`badge ${ticket.status==='ready'?'success':'warning'}`}>{minutes} min</span></div>
          {order?.customer_name&&<small style={{display:'flex',alignItems:'center',gap:6,marginTop:8}}><UserRound size={14}/>{order.customer_name}</small>}
          <div className="ticket-items" style={{marginTop:12}}>{items.map(item=><div key={item.id} style={{display:'flex',justifyContent:'space-between',gap:12}}><strong>{Number(item.quantity)}x {item.product_name}</strong>{item.notes&&<small>{item.notes}</small>}</div>)}</div>
          <small>Recebido às {new Date(ticket.created_at).toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})}</small>
          {ticket.status==='ready'&&<div className="notice" style={{margin:'12px 0 0',padding:'10px 12px'}}>✓ Pronto no balcão — aguardando retirada do garçom.</div>}
        </div>
        <button onClick={()=>void advance(ticket)} disabled={updating===ticket.id}>{updating===ticket.id?'Atualizando…':actionLabels[ticket.status]}</button>
      </article>
    })}</div>:<div className="panel empty-state"><GlassWater size={42}/><strong>Nenhuma bebida aguardando</strong><p>Cervejas e refrigerantes enviados pelas mesas aparecerão aqui automaticamente.</p><CheckCircle2 size={20}/></div>}
  </>
}
